import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient, voiceRequestUrl } from './_shared';
import { mapVoiceSessionToConversation } from './conversations';
import type { ConversationFullDetail } from './conversationFullDetail';

// ── Voice Session lifecycle ───────────────────────────────────────────

export type VoiceSessionCreateInput = {
  campaign_id: string;
  channel?: 'webrtc' | 'call';
  recipient_token?: string;
};

export type VoiceSessionRecord = {
  session_id: string;
  status: string;
  channel: string;
  campaign_id: string;
  created_at?: string;
};

/** `POST /api/voice/session` — create a voice session (WebRTC or Call). */
export async function createVoiceSession(input: VoiceSessionCreateInput, axiosInstance?: AxiosInstance): Promise<VoiceSessionRecord> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.voice}/session`;
  const response = await client.post<VoiceSessionRecord>(url, input);
  return response.data;
}

/** `GET /api/voice/session/{session_id}` — session details with transcript. */
export async function fetchVoiceSession(sessionId: string, axiosInstance?: AxiosInstance): Promise<ConversationFullDetail | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.voice}/session/${sessionId}`;
  const response = await client.get<any>(url);
  if (!response.data) return null;
  return { ...mapVoiceSessionToConversation(response.data), summary: null, channel: '', recipient_token: null, creative_token: null, events: [], contacts: [], appointments: [], callbacks: [], signals: [] };
}

/** `POST /api/voice/session/{session_id}/end` — end session and trigger post-call pipeline. */
export async function endVoiceSession(sessionId: string, axiosInstance?: AxiosInstance): Promise<{ ok: boolean }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.voice}/session/${sessionId}/end`;
  await client.post(url, {});
  return { ok: true };
}

/** Same endpoint as endVoiceSession, fired via navigator.sendBeacon so it
 * survives a tab close/navigation-away that would cancel a normal fetch. */
export function endVoiceSessionBeacon(sessionId: string): boolean {
  if (typeof navigator === 'undefined' || !navigator.sendBeacon) return false;
  const url = voiceRequestUrl(`${apiConfig.voiceEndpoints.voice}/session/${sessionId}/end`);
  const blob = new Blob(['{}'], { type: 'application/json' });
  return navigator.sendBeacon(url, blob);
}

export interface TransferStatusResult {
  session_id: string;
  transfer_status: 'dialing' | 'ready' | 'active' | 'ended' | null;
  conference_name: string | null;
  started_at: string | null;
}

/** `GET /api/voice/session/{session_id}/transfer-status` — poll warm transfer status. */
export async function fetchTransferStatus(sessionId: string, axiosInstance?: AxiosInstance): Promise<TransferStatusResult> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.voice}/session/${sessionId}/transfer-status`;
  const response = await client.get<TransferStatusResult>(url);
  return response.data;
}

export interface TransferTokenResult {
  session_id: string;
  identity: string;
  conference_name: string;
  token: string;
  ttl_seconds: number;
}

/** `POST /api/voice/session/{session_id}/transfer-token` — mint Twilio Voice access token. */
export async function mintTransferToken(sessionId: string, axiosInstance?: AxiosInstance): Promise<TransferTokenResult> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.voice}/session/${sessionId}/transfer-token`;
  const response = await client.post<TransferTokenResult>(url, {});
  return response.data;
}

/** `GET /api/voice/landing/{campaign_id}` — get campaign config for landing page. */
export async function fetchVoiceLandingConfig(campaignId: string, axiosInstance?: AxiosInstance): Promise<any> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.voice}/landing/${campaignId}`;
  const response = await client.get<any>(url);
  return response.data;
}
